/**
 * The two reads of the person record that more than one card draws from (D580): the analytics summary (the KPI row, the
 * activity card and the ratings card) and the client sessions (the "last access" KPI and the devices card). The page reads each
 * ONCE and hands the state down, so a card and its KPI can never disagree and a refused read is one refusal, not three.
 */
import { type AccessSessionsView, type CustomerSummary, type DirectoryRealm, type OperatorDriverInsights } from '@vello/api-client';
import { useAdmin } from '../session';
import { type CardState, useCardRead } from './person-card';

/** The sessions read looks back this many days (the access report's own ceiling is 93). */
export const PERSON_SESSIONS_DAYS = 90;
export const PERSON_SESSIONS_LIMIT = 20;

const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** The client sessions of one account over the last {@link PERSON_SESSIONS_DAYS} days, newest first (`access:audit`; admins only). */
export function usePersonSessions(userId: string, realm: DirectoryRealm): { readonly state: CardState<AccessSessionsView> | undefined; readonly reload: () => void } {
  const { runtime } = useAdmin();
  return useCardRead(
    userId === '' ? undefined : () => {
      const now = Date.now();
      return runtime.accessReport.sessions({ group: 'clients', realm: [realm], user: userId, from: day(now - (PERSON_SESSIONS_DAYS - 1) * 86_400_000), to: day(now), limit: PERSON_SESSIONS_LIMIT });
    },
    [runtime, userId, realm],
  );
}

/** The newest `lastUsedAt` among the sessions, or undefined when there is none. */
export function lastAccessOf(view: AccessSessionsView): string | undefined {
  let best: string | undefined;
  for (const row of view.rows) if (best === undefined || Date.parse(row.lastUsedAt) > Date.parse(best)) best = row.lastUsedAt;
  return best;
}

/**
 * The rider's analytics summary in the scope's market (`GET /v1/analytics/customers/:id/summary`, the analytics database only). Held back
 * (state `undefined`) on a driver record and while the console has no market in scope — the cards then say so instead of reading
 * a number the server cannot give.
 */
export function usePersonSummary(userId: string, realm: DirectoryRealm): { readonly state: CardState<CustomerSummary> | undefined; readonly reload: () => void; readonly market: string | undefined } {
  const { runtime, scope } = useAdmin();
  const market = scope.current.city;
  const read = realm === 'RIDER' && userId !== '' && market !== undefined ? () => runtime.customerInsights.summary(userId, market) : undefined;
  const { state, reload } = useCardRead(read, [runtime, userId, realm, market]);
  return { state, reload, market };
}

/**
 * D619: a provider's own numbers in the scope's market (`GET /v1/analytics/drivers/:id/insights`, the analytics database only — never an
 * operational read). Held back on a rider record and while the console has no market in scope, like {@link usePersonSummary}.
 */
export function usePersonDriverInsights(userId: string, realm: DirectoryRealm): { readonly state: CardState<OperatorDriverInsights> | undefined; readonly reload: () => void } {
  const { runtime, scope } = useAdmin();
  const market = scope.current.city;
  const read = realm === 'DRIVER' && userId !== '' && market !== undefined ? () => runtime.customerInsights.driver(userId, market) : undefined;
  return useCardRead(read, [runtime, userId, realm, market]);
}
